/**
 * Atelier Royal: portrait-creator.js: <ar-creator>, the commission flow on /pages/create
 * (PLAN §8, spec §20–§21, §52, §57–§61, §67–§68). Backend contract: backend/ARCHITECTURE.md §3
 * (app proxy {apiBase}: session, styles, uploads, photo-check, generations, jobs, projects,
 * print-jobs). Markup, strings and the style cards come from sections/portrait-creator.liquid;
 * the variant map from snippets/variant-map-json (#ARVariantMap). Nothing user-facing is written
 * here: every message is a translated string from Liquid, and API errors map to those by code.
 *
 * Start-up: GET {apiBase}/config first (maintenance flag, photos per subject, photo limits, retries,
 * poll interval, style handles); a maintenance flag shows a notice instead of the steps, and a plain
 * 404 (no backend behind the app proxy) shows the opening-soon panel at once, without a session call.
 * Step 1 is painted from the HTML (the page's LCP, audit AR-15); only the progress bar and a later
 * step stay invisible (data-booting) until that first answer. Without a /config answer the
 * built-in defaults below apply (they equal the backend's at the time of writing). With Theme settings
 * > Creator status "Opening soon" the section prints the panel itself and this module is not loaded.
 *
 * Steps: subject (family: 3 or 4 people) → upload (→ photo check) → style → generate → preview →
 * format → size → prepare (physical only) → cart. `?subject=`, `?style=`, `?variant=` preselect;
 * `?attach=` binds the portrait to a paid order line (portrait-missing email, or `&reason=recreate`
 * for the free re-creation) instead of adding it to the bag.
 * Resume: { token, project id, step, choices } in localStorage (spec §71), never a URL.
 *
 * Print files are private (audit AR-05, backend/ARCHITECTURE.md §3b): a ready print job answers
 * `print_ref`, an opaque reference that serves nothing (403). It goes into `_royal_portrait` and the
 * cart attribute `royal_portrait` exactly as received, with `_sig` over it; it is only ever compared as
 * a string, never fetched, shown or used as an image. A bag from before that change holds the old
 * file URL instead: it stays as it is (the backend accepts both), and adding the same print again
 * keeps that line rather than asking to replace it.
 */
import { track, trackVariant, announce, toast, openCart, getCart, routes, theme } from '@theme/global';
import { blurReveal } from '@theme/animations';

const STORE_KEY = 'atelier-royal:creator';
const MAX_AGE_MS = 30 * 86400000;
const POLL_MS = 2500;
const LATER_MS = 75000;
const PHYSICAL = ['print', 'canvas'];
const FORMAT_ORDER = ['digital', 'print', 'canvas'];
// `max` = photos per subject (default of config photos.max_per_subject); `count` = sitters.
const SUBJECTS = {
  person: { max: 1, count: 1, face: true },
  couple: { max: 2, count: 2, face: true },
  family: { max: 4, count: 3, face: true },
  pet: { max: 1, count: 1, face: false },
  person_pet: { max: 2, count: 2, face: false }, // face_enhance off at launch (PLAN §8)
};
const FAMILY_MIN = 3; // a family portrait has 3 or 4 sitters (the customer says which)
const FAMILY_MAX = 4;
const clampFamily = (n) => Math.min(FAMILY_MAX, Math.max(FAMILY_MIN, Math.round(Number(n)) || FAMILY_MIN));
const SUBJECT_ALIASES = { dog: 'pet', cat: 'pet', pets: 'pet', animal: 'pet', people: 'person', 'person-pet': 'person_pet', personpet: 'person_pet' };
const BOOT_UNAVAILABLE = ['unavailable', 'network', 'internal_error', 'not_configured', 'unauthorized', 'invalid_request', 'not_found'];

/* ---------- helpers ---------- */
/* Shopify's `t` filter HTML-escapes translations (d'art → d&#39;art) and the section passes them
   through `json`; this module only ever writes text with textContent, so decode them once here. */
const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
const decodeEntities = (v) => {
  if (typeof v === 'string') return v.replace(/&(?:amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m]);
  if (Array.isArray(v)) return v.map(decodeEntities);
  if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, decodeEntities(x)]));
  return v;
};
const readJSON = (el) => {
  try {
    return decodeEntities(JSON.parse(el?.textContent || 'null'));
  } catch {
    return null;
  }
};
const fill = (template, values) => String(template ?? '').replace(/\[(\w+)\]/g, (m, key) => (key in values ? String(values[key]) : m));
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isOffline = () => navigator.onLine === false;
const onceOnline = () =>
  new Promise((resolve) => {
    if (!isOffline()) return resolve();
    window.addEventListener('online', () => resolve(), { once: true });
  });

function randomToken(bytes = 16) {
  const data = crypto.getRandomValues(new Uint8Array(bytes));
  let s = '';
  data.forEach((b) => {
    s += String.fromCharCode(b);
  });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * The `_royal_portrait` value of a ready print job: `print_ref` (AR-05), else `url` (the same value
 * since backend B2; the only field of an older backend, kept for a backend rollback). Opaque: never
 * fetched, shown or used as an image. '' when the job has none (not ready).
 */
function printRefOf(data) {
  const ref = data?.print_ref || data?.url;
  return typeof ref === 'string' ? ref : '';
}

/** A bag line holds this print job's file: the same reference, or a line from before AR-05 with the same print job. */
function holdsPrint(item, job) {
  const props = item?.properties || {};
  if (!job?.ref) return false;
  return props._royal_portrait === job.ref || Boolean(job.job_id && props._print_job_id === job.job_id);
}

function dims(value) {
  if (value && typeof value === 'object' && value.width && value.height) return [Number(value.width), Number(value.height)];
  const m = /^\s*(\d+(?:\.\d+)?)\s*[x×:*]\s*(\d+(?:\.\d+)?)/i.exec(String(value ?? ''));
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/** Crop guide over the 2:3 preview: the default (centred, 25 % of the slack above) print crop. */
export function cropBox(w, h) {
  const base = 2 / 3;
  const r = w / h;
  if (!(r > 0) || Math.abs(r - base) / base < 0.005) return { x: 0, y: 0, w: 1, h: 1 };
  if (r > base) {
    const bh = base / r;
    return { x: 0, y: (1 - bh) * 0.25, w: 1, h: bh };
  }
  const bw = r / base;
  return { x: (1 - bw) / 2, y: 0, w: bw, h: 1 };
}

function readVariantMap() {
  const raw = readJSON(document.getElementById('ARVariantMap')) || {};
  const source = raw.variants && typeof raw.variants === 'object' ? raw.variants : raw;
  const map = {};
  Object.entries(source).forEach(([key, v]) => {
    if (!v || typeof v !== 'object' || !v.id) return;
    const type = v.product_type || (key === 'digital' ? 'digital' : key.split('_')[0]);
    const size = type === 'digital' ? 'digital' : String(v.size || key.split('_')[1] || '');
    map[key] = { ...v, key, product_type: type, size };
  });
  return map;
}

class ApiError extends Error {
  constructor(code, status = 0) {
    super(code);
    this.code = code;
    this.status = status;
  }
}

/** App-proxy client: JSON in/out, session token in the X-Atelier-Token header and the POST body. */
class AtelierApi {
  constructor(base) {
    this.base = String(base || '/apps/atelier').replace(/\/+$/, '');
    this.token = null;
  }

  async call(path, { method = 'POST', body = {}, timeout = 30000 } = {}) {
    const headers = { Accept: 'application/json' };
    const init = { method, headers, credentials: 'same-origin' };
    if (this.token) headers['X-Atelier-Token'] = this.token;
    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(this.token ? { ...body, token: this.token } : body);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    init.signal = controller.signal;
    let res;
    try {
      res = await fetch(`${this.base}/${path}`, init);
    } catch {
      throw new ApiError('network');
    } finally {
      clearTimeout(timer);
    }
    let data = null;
    if ((res.headers.get('content-type') || '').includes('json')) {
      try {
        data = await res.json();
      } catch {
        data = null;
      }
    }
    if (res.ok && data && data.ok !== false) return { status: res.status, data };
    const code = data?.error?.code || (res.status === 404 || res.status >= 500 ? 'unavailable' : 'internal_error');
    const error = new ApiError(code, res.status);
    // A 404 without a JSON error body: the app proxy has no backend behind it (not deployed yet).
    error.noBackend = res.status === 404 && !data;
    throw error;
  }
}

/** Minimal cart add used only when assets/cart.js is not available. */
async function fallbackAddToCart({ variantId, properties, attributes, replacePhysical, isPhysical, isSame }) {
  const post = async (url, body) => {
    const res = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError('cart', res.status);
    return res.json();
  };
  const before = await getCart();
  // The same print is already in the bag (possibly a line from before AR-05): keep that line as it is.
  const kept = (before?.items || []).find((item) => String(item.variant_id) === String(variantId) && isSame?.(item)) || null;
  if (replacePhysical) {
    const updates = {};
    (before?.items || []).forEach((item) => {
      if (item !== kept && isPhysical(item)) updates[item.key] = 0;
    });
    if (Object.keys(updates).length) await post(routes.cart_update_url || '/cart/update', { updates });
  }
  if (!kept) await post(routes.cart_add_url || '/cart/add', { items: [{ id: Number(variantId), quantity: 1, properties }] });
  const keptRef = kept?.properties?._royal_portrait;
  const cart = await post(routes.cart_update_url || '/cart/update', { attributes: keptRef ? { ...attributes, royal_portrait: keptRef } : attributes });
  document.dispatchEvent(new CustomEvent('ar:cart:updated', { detail: { cart } }));
  return cart;
}

/* ---------- <ar-creator> ---------- */
class ARCreator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.arReady) return;
    this.dataset.arReady = 'true';
    this.config = readJSON(this.querySelector('[data-creator-config]')) || {};
    this.s = this.config.strings || {};
    this.map = readVariantMap();
    this.api = new AtelierApi(this.config.apiBase || theme.creator?.apiBase);
    this.locale = this.config.locale || theme.locale || document.documentElement.lang || 'en';
    this.params = this.#readParams();
    this.attach = this.params.attach;
    this.lockedVariant = this.attach ? this.#variantById(this.params.variant) : null;
    this.presetVariant = this.attach ? null : this.#variantById(this.params.variant);
    // Backend limits (GET {api}/config); these defaults apply until, or without, an answer.
    this.maxPhotos = Object.fromEntries(Object.entries(SUBJECTS).map(([type, cfg]) => [type, cfg.max]));
    this.photoLimits = {};
    this.pollMs = POLL_MS;
    this.familyCount = FAMILY_MIN;
    this.#reset();
    this.pollRun = 0;
    this.busy = false;

    if (!this.bound) {
      // Element-level listeners: bound once, even if the element is moved in the DOM.
      this.bound = true;
      this.addEventListener('click', (event) => this.#onClick(event));
      this.addEventListener('change', (event) => this.#onChange(event));
      this.#bindDropzone();
    }
    this.onSticky = (event) => {
      if (event.target === this.cta) this.#primaryButton()?.click();
    };
    this.onOnline = () => this.#status(this.step, '');
    this.onOffline = () => {
      if (this.step === 'generate' || this.step === 'prepare') this.#status(this.step, this.s.generate?.offline);
    };
    // The bag changed (this page, the drawer): the cart step says "Added to your bag" only while the line is there.
    this.onCartUpdated = (event) => {
      if (this.step !== 'cart') return;
      const cart = event.detail?.cart;
      if (Array.isArray(cart?.items)) {
        this.bagRead = (this.bagRead || 0) + 1; // newer than any /cart.js read still in flight
        this.#syncAdded(cart);
      } else this.#checkBag();
    };
    document.addEventListener('ar:sticky-cta:action', this.onSticky);
    document.addEventListener('ar:cart:updated', this.onCartUpdated);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    customElements.whenDefined?.('ar-sticky-cta').then(() => {
      const cta = document.querySelector('ar-sticky-cta');
      if (!cta || !this.isConnected) return;
      this.cta = cta;
      const action = cta.querySelector('[data-ar-sticky-action]');
      this.ctaDefaults = {
        label: cta.querySelector('[data-ar-sticky-label]')?.textContent || '',
        note: cta.querySelector('[data-ar-sticky-note]')?.textContent || '',
        href: action?.getAttribute('href') || undefined,
        track: action?.getAttribute('data-ar-track') || null,
      };
      // While it acts as the step button it must not report click_create_portrait (spec §57).
      action?.removeAttribute('data-ar-track');
      this.#syncCta();
    });
    this.boot();
  }

  disconnectedCallback() {
    this.pollRun += 1;
    document.removeEventListener('ar:sticky-cta:action', this.onSticky);
    document.removeEventListener('ar:cart:updated', this.onCartUpdated);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    this.photos?.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    if (this.ctaDefaults && this.cta?.configure) {
      const { track: trackName, ...defaults } = this.ctaDefaults;
      this.cta.configure({ ...defaults, action: null, suppressed: false, disabled: false, busy: false });
      if (trackName) this.cta.querySelector('[data-ar-sticky-action]')?.setAttribute('data-ar-track', trackName);
    }
    delete this.dataset.arReady;
  }

  #reset() {
    this.projectId = null;
    this.projectSubject = null;
    this.projectCount = 0;
    this.projectSitters = 0;
    this.projectReady = false;
    this.photos = [];
    this.photoSeq = 0;
    this.attempts = [];
    this.attemptId = null;
    this.activeJobId = null;
    this.retriesLeft = null;
    this.printJob = null;
    this.added = false;
    this.warned = new Set();
    this.resumeLater = null;
  }

  #readParams() {
    const q = new URLSearchParams(window.location.search);
    let subject = String(q.get('subject') || '').toLowerCase();
    subject = SUBJECT_ALIASES[subject] || subject;
    const attach = q.get('attach') || '';
    return {
      reason: q.get('reason') === 'recreate' ? 'recreate' : null,
      subject: SUBJECTS[subject] ? subject : null,
      style: String(q.get('style') || '').toLowerCase().replace(/[^a-z0-9-]/g, '') || null,
      variant: String(q.get('variant') || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '').replace(/\D/g, '') || null,
      attach: /^[A-Za-z0-9_-]{16,128}$/.test(attach) ? attach : null,
    };
  }

  #variantById(id) {
    if (!id) return null;
    return Object.values(this.map).find((v) => String(v.id) === String(id)) || null;
  }

  /* ---------- boot, session, resume ---------- */
  async boot() {
    this.#query('[data-panel="attach"]', (el) => {
      el.hidden = !this.attach;
    });
    this.#query('[data-attach-note]', (el) => {
      el.hidden = el.dataset.attachNote !== (this.params.reason || 'order');
    });
    const preview = this.config.editorPreview;
    if (preview === 'unavailable' || preview === 'maintenance') return this.#showUnavailable(preview);

    const saved = this.#load();
    this.resumeLater = saved?.projectId || null; // kept in storage until the project is resolved
    this.savedToken = saved?.token || null; // reused by the first #ensureSession(), kept by #save() until then
    // Step 1 is painted from the HTML (AR-15): a sitter tapped before this script ran is kept, not cleared.
    const tapped = this.querySelector('[data-subject]:checked')?.value;
    this.subject =
      this.params.subject || (saved?.subject && SUBJECTS[saved.subject] ? saved.subject : null) || (tapped && SUBJECTS[tapped] ? tapped : null);
    this.familyCount = clampFamily(saved?.sitters);
    this.styleHandle = this.params.style || saved?.style || null;
    this.gallery = Boolean(saved?.gallery);
    // The latest intent wins: an order to attach to, then a format/size picked on the product page, then the saved choice.
    const variant = this.lockedVariant || this.presetVariant;
    if (variant) {
      this.format = variant.product_type;
      this.variantKey = variant.key;
    } else if (saved?.format) {
      this.format = saved.format;
      this.variantKey = saved.variantKey && this.map[saved.variantKey] ? saved.variantKey : null;
    }
    // ?subject= skips to the photo, except for a family: its size (3 or 4 people) is asked first.
    const skip = this.params.subject && this.params.subject !== 'family' && !saved?.projectId;
    this.goTo(skip ? 'upload' : 'subject', { focus: false });
    this.stylesReady = this.#loadStyles();

    const config = await this.#loadConfig();
    if (config?.maintenance) return this.#showUnavailable('maintenance');
    // No backend behind the app proxy: the panel at once, without a session request (perf-a11y-audit P0-1).
    if (config?.unavailable) return this.#showUnavailable();
    this.#booted();
    this.#applyConfig(config);

    // No session on page view (bots, previews and refreshes no longer open one): #ensureSession() opens it at
    // the first real action (the photo "Continue"), resuming the stored token. Boot only opens it to restore a
    // saved project. A customer who moved on before /config answered may already hold a session: keep it.
    const ok = this.api.token || !saved?.projectId ? true : await this.#startSession(saved.token, true);
    if (!ok) return;
    this.#idle(() => this.#uploadLib());
    track('creator_started', { resumed: Boolean(saved?.projectId), preselected_style: Boolean(this.params.style), preselected_subject: Boolean(this.params.subject) });
    if (saved?.projectId) await this.#resume(saved);
  }

  /** GET {api}/config (no session, cacheable 60 s). null → keep the defaults (older backend, network). */
  async #loadConfig() {
    try {
      const { data } = await this.api.call('config', { method: 'GET', timeout: 6000 });
      return data;
    } catch (error) {
      if (error.code === 'maintenance') return { maintenance: true };
      return error.noBackend ? { unavailable: true } : null;
    }
  }

  /** The first backend answer is in: show the progress bar and the current step (sections/portrait-creator.liquid data-booting). */
  #booted() {
    this.removeAttribute('data-booting');
  }

  #applyConfig(c) {
    if (!c || typeof c !== 'object') return;
    const num = (v) => (Number(v) > 0 ? Number(v) : null);
    const photos = c.photos || {};
    const perProject = num(photos.max_per_project) || 4;
    Object.keys(this.maxPhotos).forEach((type) => {
      const n = num(photos.max_per_subject?.[type]);
      if (n) this.maxPhotos[type] = Math.floor(Math.min(n, perProject));
    });
    this.photoLimits = {
      types: Array.isArray(photos.accepted_mimes) && photos.accepted_mimes.length ? photos.accepted_mimes.map(String) : null,
      maxBytes: num(photos.max_bytes),
      maxEdge: num(photos.resize_long_edge),
      minEdge: num(photos.hard_min_short_edge), // refused below (photo_too_small)
      warnEdge: num(photos.min_short_edge), // warned below (low_resolution)
    };
    this.pollMs = Math.max(1000, num(c.poll_after_ms) || POLL_MS);
    if (Array.isArray(c.style_handles) && c.style_handles.length) this.configStyles = new Set(c.style_handles.map(String));
    const allowance = this.s.preview?.allowance?.[num(c.caps?.retries)];
    this.#query('[data-allowance]', (el) => {
      if (allowance) el.textContent = allowance;
    });
    this.#query('[data-photo-count]', (el) => {
      const text = this.s.photoCount?.[this.maxPhotos[el.dataset.photoCount]];
      if (text) el.textContent = text;
    });
    if (this.step === 'upload') this.#renderUpload();
    if (this.step === 'style') this.#renderStyles();
  }

  /**
   * POST {api}/session, one request at a time. Step 1 is usable from first paint (AR-15), so a customer
   * can reach the photos before boot's request answers: a second caller waits for the request in flight
   * instead of opening another session, whose token would replace the first after a project was
   * created under it (the next calls would then fail as session expired).
   */
  #startSession(token, boot = false) {
    if (!this.sessionPromise) {
      this.sessionPromise = this.#requestSession(token, boot).finally(() => {
        this.sessionPromise = null;
      });
    }
    return this.sessionPromise;
  }

  async #requestSession(token, boot) {
    try {
      const { data } = await this.api.call('session', { body: { token: token || undefined, locale: this.locale } });
      this.api.token = data.token;
      this.sessionReady = true;
      this.#save();
      return true;
    } catch (error) {
      if (boot && isOffline()) {
        this.#alert(this.s.errors?.network);
        await onceOnline();
        return this.#requestSession(token, boot);
      }
      if (boot && error.code === 'maintenance') {
        this.#showUnavailable('maintenance');
        return false;
      }
      if (boot && BOOT_UNAVAILABLE.includes(error.code)) {
        this.#showUnavailable();
        return false;
      }
      this.#alert(this.#message(error.code));
      return false;
    }
  }

  async #ensureSession() {
    if (this.api.token) return true;
    // Boot's request may be in flight: #startSession then waits for it. Else the stored session is resumed.
    return this.#startSession(this.savedToken || null);
  }

  async #resume(saved) {
    let project;
    try {
      ({ data: { project } } = await this.api.call(`projects/${saved.projectId}`));
    } catch (error) {
      if (error.code === 'not_found' || error.code === 'unauthorized') {
        this.resumeLater = null; // expired or deleted: start afresh
        this.#save();
      } else this.#alert(this.#message(error.code)); // transient: the saved project stays for next time
      return;
    }
    this.resumeLater = null;
    if (!project) return this.#save();
    this.projectId = project.project_id || project.id;
    this.subject = project.subject_type || this.subject;
    this.projectSubject = this.subject;
    this.projectCount = project.photo_count || 0;
    this.projectSitters = project.subject_count || 0;
    if (this.subject === 'family' && this.projectSitters) this.familyCount = clampFamily(this.projectSitters);
    this.retriesLeft = typeof project.retries_left === 'number' ? project.retries_left : null;
    this.attempts = (project.attempts || []).map((a) => ({ ...a })).sort((a, b) => (a.attempt || 0) - (b.attempt || 0));
    this.projectReady = project.status === 'uploaded' || this.attempts.length > 0;
    (project.photos || []).forEach((p) => (p.warnings || []).length && this.warned.add(p.index));

    const active = this.attempts.find((a) => a.status === 'queued' || a.status === 'generating');
    const ready = this.attempts.filter((a) => a.status === 'ready');
    let step = 'upload';
    if (active) step = 'generate';
    else if (ready.length) {
      this.attemptId = ready.some((a) => a.job_id === saved.attemptId) ? saved.attemptId : ready[ready.length - 1].job_id;
      step = ['preview', 'format', 'size', 'prepare', 'cart'].includes(saved.step) ? saved.step : 'preview';
      const variant = this.#variant();
      if (step !== 'preview' && !this.format) step = 'format';
      if (['size', 'prepare', 'cart'].includes(step) && PHYSICAL.includes(this.format) && !variant) step = 'size';
      if (step === 'size' && this.format === 'digital') step = 'cart';
      if (step === 'cart' && !variant) step = 'format';
      if (step === 'cart' && PHYSICAL.includes(this.format)) step = 'prepare';
    } else if (this.projectReady) step = 'style';
    else this.#reset();

    if (step === 'upload' && this.step === 'upload') return;
    // Restored before goTo, whose #save would otherwise store no print job while the first poll waits:
    // a reload in that window started a new print job instead of polling the saved one.
    if (!active && step === 'prepare' && saved.printJobId) {
      this.printJob = { job_id: saved.printJobId, variant_key: this.variantKey, generation_id: this.attemptId };
    }
    this.#showResume(step !== 'upload');
    this.goTo(step, { focus: false });
    if (active) {
      this.activeJobId = active.job_id;
      this.#pollGeneration(active.job_id);
    } else if (step === 'prepare') {
      if (saved.printJobId) this.#pollPrint(saved.printJobId);
      else this.#startPrint();
    }
  }

  #showResume(show) {
    this.#query('[data-panel="resume"]', (el) => {
      el.hidden = !show;
    });
  }

  /** Replaces the flow by the "opening soon" panel (backend unreachable) or the maintenance notice. */
  #showUnavailable(panel = 'unavailable') {
    this.unavailable = true;
    this.#booted();
    this.#query('[data-panel="unavailable"], [data-panel="maintenance"]', (el) => {
      el.hidden = el.dataset.panel !== panel;
    });
    ['[data-steps]', '[data-bar]', '[data-panel="resume"]', '[data-panel="attach"]'].forEach((sel) =>
      this.#query(sel, (el) => {
        el.hidden = true;
      })
    );
    this.pollRun += 1;
    this.#syncCta();
    document.dispatchEvent(new Event('ar:cta:refresh'));
    if (panel === 'maintenance') announce(this.s.errors?.maintenance);
  }

  /* ---------- persistence ---------- */
  #load() {
    try {
      const data = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (!data || data.v !== 1 || Date.now() - (data.savedAt || 0) > MAX_AGE_MS) return null;
      return data;
    } catch {
      return null;
    }
  }

  #save() {
    if (this.unavailable) return;
    // While boot restores a saved project, keep its record as it is until the session answers.
    if (!this.sessionReady && this.resumeLater) return;
    const data = {
      v: 1,
      savedAt: Date.now(),
      token: this.api.token || this.savedToken || null, // choices made before the first session keep the stored token
      projectId: this.projectId || this.resumeLater || null,
      step: this.step,
      subject: this.subject,
      sitters: this.familyCount,
      style: this.styleHandle,
      format: this.format,
      variantKey: this.variantKey,
      attemptId: this.attemptId,
      activeJobId: this.activeJobId,
      printJobId: this.printJob?.job_id || null,
      gallery: this.gallery,
    };
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch {
      /* private mode: resume is a convenience */
    }
  }

  /* ---------- navigation ---------- */
  #sequence() {
    const digital = this.format === 'digital';
    const seq = ['subject', 'upload', 'style', 'generate', 'preview'];
    if (!this.lockedVariant) seq.push('format');
    if (!digital) {
      if (!this.lockedVariant) seq.push('size');
      seq.push('prepare');
    }
    seq.push(this.attach ? 'attached' : 'cart');
    return seq;
  }

  #backTarget() {
    const ready = this.attempts.some((a) => a.status === 'ready');
    switch (this.step) {
      case 'upload':
        return 'subject';
      case 'check':
        return 'upload';
      case 'style':
        return ready ? 'preview' : 'upload';
      case 'preview':
        return 'style';
      case 'format':
        return 'preview';
      case 'size':
        return 'format';
      case 'prepare':
        return this.lockedVariant ? 'preview' : 'size';
      case 'cart':
        return this.format === 'digital' ? 'format' : 'size';
      default:
        return null;
    }
  }

  goTo(step, { focus = true } = {}) {
    const from = this.step;
    this.step = step;
    this.querySelectorAll('[data-step]').forEach((section) => {
      section.hidden = section.dataset.step !== step;
    });
    // The customer moved on from the visible step 1 before GET /config answered: never show them an invisible step.
    if (focus && from !== undefined && from !== step) this.#booted();
    this.#clearAlert(step);
    this.#render(step);
    this.#updateBar();
    this.#syncCta();
    document.dispatchEvent(new Event('ar:cta:refresh'));
    if (focus && from !== undefined && from !== step) {
      const heading = this.querySelector(`[data-step="${step}"] [data-heading]`);
      heading?.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView?.({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      }
    }
    this.#save();
  }

  #render(step) {
    if (step === 'subject') this.#renderSubject();
    else if (step === 'upload') this.#renderUpload();
    else if (step === 'style') this.#renderStyles();
    else if (step === 'generate') this.#renderGenerate(false);
    else if (step === 'preview') this.#renderPreview();
    else if (step === 'format') this.#renderFormats();
    else if (step === 'size') this.#renderSizes();
    else if (step === 'prepare') this.#renderPrepare(false);
    else if (step === 'cart') {
      this.#renderCart();
      this.#checkBag();
    }
  }

  #updateBar() {
    const seq = this.#sequence();
    const logical = this.step === 'check' ? 'upload' : this.step;
    const current = Math.max(1, seq.indexOf(logical) + 1);
    this.#query('[data-counter]', (el) => {
      el.textContent = fill(this.s.counter, { current, total: seq.length });
    });
    this.#query('[data-step-name]', (el) => {
      el.textContent = this.s.steps?.[this.step] || '';
    });
    this.#query('[data-meter]', (el) => {
      el.style.setProperty('--progress', `${Math.round((current / seq.length) * 100)}%`);
    });
    const back = this.#backTarget();
    this.#query('[data-action="back"]', (el) => {
      el.hidden = !back;
    });
    this.#query('[data-action="start-over"]', (el) => {
      el.hidden = !(this.projectId || this.photos.length) || this.step === 'attached';
    });
  }

  back() {
    const target = this.#backTarget();
    if (!target) return;
    this.pollRun += 1; // stop watching a print job we are leaving (it keeps running server-side)
    this.goTo(target);
  }

  /* ---------- sticky CTA (PLAN §6.5 rule 2) ---------- */
  #primaryButton() {
    const section = this.querySelector(`[data-step="${this.step}"]`);
    return [...(section?.querySelectorAll('[data-primary]') || [])].find((btn) => !btn.closest('[hidden]')) || null;
  }

  #syncCta() {
    const cta = this.cta;
    if (!cta?.configure) return;
    const primary = this.unavailable ? null : this.#primaryButton();
    if (!primary) {
      cta.configure({ suppressed: true });
      return;
    }
    cta.configure({
      suppressed: false,
      action: 'creator',
      label: primary.dataset.ctaLabel || primary.textContent.trim(),
      note: '',
      disabled: primary.getAttribute('aria-disabled') === 'true',
      busy: primary.getAttribute('aria-busy') === 'true',
    });
  }

  /* ---------- events ---------- */
  #onClick(event) {
    const el = event.target.closest('[data-action]');
    if (!el || !this.contains(el)) return;
    if (el.getAttribute('aria-disabled') === 'true' || el.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }
    const action = el.dataset.action;
    switch (action) {
      case 'back':
        return this.back();
      case 'next':
        return this.#next(el);
      case 'sitters':
        return this.#stepSitters(Number(el.dataset.delta) || 0);
      case 'remove-photo':
        return this.#removePhoto(Number(el.dataset.photo));
      case 'choose-another':
        return this.#chooseAnother();
      case 'use-anyway':
        return this.goTo('style');
      case 'generate':
        return this.#generate(el, { from: 'style' });
      case 'retry': // preview: the version on screen; after a failure: the style that failed
        return this.#generate(el, { from: this.step, style: (this.step === 'preview' && this.#current()?.style) || this.styleHandle });
      case 'other-style':
        return this.goTo('style');
      case 'new-photo':
        return this.#newPhoto();
      case 'compare':
        return this.#openCompare(el);
      case 'choose-version':
        this.#dialog('compare')?.hide();
        return this.#chooseVersion(el.dataset.job);
      case 'accept':
        return this.#accept(el);
      case 'prepare-retry':
        return this.#startPrint();
      case 'add-to-cart':
        return this.#addToCart(el);
      case 'attach-digital':
        return this.#attachDigital(el);
      case 'view-bag':
        return openCart(el);
      case 'start-over':
        return this.#dialog('startover')?.show(el);
      case 'start-over-confirm':
        this.#dialog('startover')?.hide();
        return this.#startOver();
      case 'resume-continue':
        this.#showResume(false);
        return this.querySelector(`[data-step="${this.step}"] [data-heading]`)?.focus();
      case 'resume-new':
        this.#showResume(false);
        return this.#startOver();
      default:
        return undefined;
    }
  }

  #onChange(event) {
    const el = event.target;
    if (el.matches('[data-subject]')) {
      this.subject = el.value;
      this.#clearAlert('subject');
      this.#renderSitters();
    } else if (el.matches('[data-file-input]')) {
      const files = [...(el.files || [])];
      el.value = '';
      this.#addFiles(files);
    } else if (el.matches('[data-rights]')) {
      if (el.checked) this.#fieldError('rights', false);
    } else if (el.matches('[data-gallery]')) {
      this.gallery = el.checked;
      this.#save();
    } else if (el.matches('[data-style]')) {
      this.styleHandle = el.value;
      this.#clearAlert('style');
      this.#query('[data-style-note]', (note) => {
        note.hidden = true;
      });
    } else if (el.matches('[data-format]')) {
      this.#clearAlert('format');
      this.#toggleConsent(el.value === 'digital');
    } else if (el.matches('[data-digital-consent]')) {
      if (el.checked) this.#fieldError('consent', false);
    } else if (el.matches('[data-size]')) {
      this.#clearAlert('size');
      this.#updateCrop(el.value);
    } else if (el.matches('[data-version]')) {
      this.#chooseVersion(el.value);
    }
  }

  #next(el) {
    if (this.step === 'subject') return this.#nextFromSubject();
    if (this.step === 'upload') return this.#submitPhotos(el);
    if (this.step === 'format') return this.#nextFromFormat(el);
    if (this.step === 'size') return this.#nextFromSize();
    return undefined;
  }

  async #guard(el, fn) {
    if (this.busy) return;
    this.busy = true;
    el?.setAttribute('aria-busy', 'true');
    this.#syncCta();
    try {
      await fn();
    } finally {
      this.busy = false;
      el?.removeAttribute('aria-busy');
      this.#syncCta();
    }
  }

  /* ---------- UI utilities ---------- */
  #query(selector, fn) {
    this.querySelectorAll(selector).forEach(fn);
  }

  #dialog(name) {
    return this.querySelector(`[data-dialog="${name}"]`);
  }

  #clone(name) {
    const tpl = this.querySelector(`template[data-tpl="${name}"]`);
    return tpl ? tpl.content.firstElementChild.cloneNode(true) : null;
  }

  #alert(message, step = this.step) {
    const box = this.querySelector(`[data-step="${step}"] [data-alert]`);
    if (!box) {
      if (message) announce(message, 'assertive');
      return;
    }
    box.replaceChildren();
    if (!message) return;
    const node = this.#clone('alert');
    if (!node) {
      box.textContent = message;
      return;
    }
    node.querySelector('[data-text]').textContent = message;
    box.append(node);
  }

  #clearAlert(step = this.step) {
    this.querySelector(`[data-step="${step}"] [data-alert]`)?.replaceChildren();
  }

  /**
   * A validation message on the visible step: shown, brought into view, and focus on the control to fix
   * (`control`, optional). On mobile the step button sits far below the alert slot at the top of the step,
   * so without this the button seemed to do nothing (wave-2 #8).
   */
  #invalid(message, control = null) {
    this.#alert(message);
    this.#reveal(control);
  }

  /**
   * Scrolls the visible step's alert into view when it is under the sticky header, above the viewport or
   * below it (or under the sticky button), then moves focus to `control` without a second scroll. The
   * role="alert" box itself is never focused (it would be announced twice). Nothing happens for a step
   * that is not on screen, so a background or late alert never moves the page.
   */
  #reveal(control = null, step = this.step) {
    if (step !== this.step) return;
    const section = this.querySelector(`[data-step="${step}"]`);
    const box = section?.querySelector('[data-alert]');
    if (!section || section.hidden || this.hidden || !box?.childNodes.length) return;
    const rect = box.getBoundingClientRect();
    const top = Math.max(0, document.querySelector('.ar-header-section')?.getBoundingClientRect().bottom || 0);
    const cta = document.querySelector('.ar-sticky-cta[data-visible]');
    const bottom = window.innerHeight - (cta ? cta.getBoundingClientRect().height : 0);
    if (rect.top < top || rect.bottom > bottom) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      box.scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
    control?.focus({ preventScroll: true });
  }

  /** The first enabled, shown choice of a step's radio group (`attr` is its data attribute), to focus on a validation error. */
  #firstChoice(step, attr) {
    return [...this.querySelectorAll(`[data-step="${step}"] [${attr}]`)].find((el) => !el.disabled && !el.closest('[hidden]')) || null;
  }

  #status(step, message) {
    this.#query(`[data-step="${step}"] [data-status]`, (el) => {
      el.textContent = message || '';
    });
  }

  #fieldError(name, show) {
    const input = this.querySelector(`[data-${name === 'rights' ? 'rights' : 'digital-consent'}]`);
    const error = this.querySelector(`[data-error-for="${name}"]`);
    if (!input || !error) return;
    error.hidden = !show;
    if (show) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', error.id);
      input.focus();
    } else {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  #message(code) {
    const errors = this.s.errors || {};
    if (code === 'network' && isOffline()) return this.s.generate?.offline || errors.network;
    return errors[code] || errors.generic || '';
  }

  #idle(fn) {
    (window.requestIdleCallback || ((cb) => setTimeout(cb, 1200)))(fn);
  }

  #uploadLib() {
    if (!this.uploadModule) {
      this.uploadModule = import(this.config.uploadModule).catch(() => {
        this.uploadModule = null;
        return null;
      });
    }
    return this.uploadModule;
  }

  async #cartAdd() {
    for (const source of ['@theme/cart', this.config.cartModule]) {
      if (!source) continue;
      try {
        const mod = await import(source);
        if (typeof mod.addPortraitToCart === 'function') return mod.addPortraitToCart;
      } catch {
        /* next source */
      }
    }
    return fallbackAddToCart;
  }

  /* ---------- step 1: subject ---------- */
  #renderSubject() {
    this.#query('[data-subject]', (input) => {
      input.checked = input.value === this.subject;
    });
    this.#renderSitters();
  }

  /** Family only: the 3–4 people stepper (buttons stay focusable at the limits: aria-disabled). */
  #renderSitters() {
    this.#query('[data-sitters]', (el) => {
      el.hidden = this.subject !== 'family';
    });
    this.#query('[data-sitters-value]', (el) => {
      el.textContent = this.s.sitters?.[this.familyCount] || String(this.familyCount);
    });
    this.#query('[data-action="sitters"]', (btn) => {
      const atLimit = Number(btn.dataset.delta) < 0 ? this.familyCount <= FAMILY_MIN : this.familyCount >= FAMILY_MAX;
      btn.setAttribute('aria-disabled', String(atLimit));
    });
  }

  #stepSitters(delta) {
    const next = clampFamily(this.familyCount + delta);
    if (next === this.familyCount) return;
    this.familyCount = next;
    this.#renderSitters();
    this.#save();
  }

  #nextFromSubject() {
    const input = this.querySelector('[data-subject]:checked');
    if (!input) return this.#invalid(this.s.subjectError, this.#firstChoice('subject', 'data-subject'));
    const subject = input.value;
    if (this.projectSubject && subject !== this.projectSubject) {
      // A different sitter means a different portrait project.
      this.projectId = null;
      this.projectReady = false;
      this.attempts = [];
      this.attemptId = null;
      this.photos.forEach((p) => {
        p.uploaded = false;
      });
    }
    this.subject = subject;
    track('subject_selected', { subject_type: subject, subject_count: this.#sitters() });
    this.goTo('upload');
  }

  /* ---------- step 2: upload ---------- */
  #subjectConfig() {
    const type = SUBJECTS[this.subject] ? this.subject : 'person';
    return { ...SUBJECTS[type], max: this.maxPhotos[type] || SUBJECTS[type].max };
  }

  #renderUpload() {
    const cfg = this.#subjectConfig();
    const multi = cfg.max > 1;
    this.#query('[data-file-input]', (input) => {
      input.multiple = multi;
    });
    this.#query('[data-heading-variant]', (el) => {
      el.hidden = el.dataset.headingVariant !== (multi ? 'multi' : 'single');
    });
    const guidance = this.subject === 'pet' ? 'pet' : this.subject === 'family' || this.subject === 'couple' ? 'group' : 'default';
    this.#query('[data-guidance]', (el) => {
      el.hidden = el.dataset.guidance !== guidance && el.dataset.guidance !== 'default';
    });
    this.#query('[data-gallery]', (el) => {
      el.checked = Boolean(this.gallery);
    });
    this.#renderPhotos();
  }

  #renderPhotos() {
    const cfg = this.#subjectConfig();
    const list = this.querySelector('[data-photo-list]');
    if (list) {
      list.replaceChildren();
      this.photos.forEach((photo, i) => {
        const item = this.#clone('photo');
        if (!item) return;
        const img = item.querySelector('img');
        img.src = photo.url;
        img.alt = fill(this.s.upload?.photoAlt, { number: i + 1 });
        item.classList.toggle('is-preparing', photo.status === 'preparing');
        item.classList.toggle('is-warned', Boolean(photo.small) || this.warned.has(photo.index));
        const remove = item.querySelector('[data-action="remove-photo"]');
        remove.dataset.photo = String(photo.id);
        remove.querySelector('[data-label]').textContent = fill(this.s.upload?.remove, { number: i + 1 });
        list.append(item);
      });
      list.hidden = !this.photos.length;
    }
    this.#query('[data-small-warning]', (el) => {
      el.hidden = !this.photos.some((p) => p.small);
    });
    const full = this.photos.length >= cfg.max;
    this.#query('[data-dropzone]', (zone) => {
      zone.classList.toggle('has-photo', this.photos.length > 0);
      zone.hidden = full && cfg.max > 1;
    });
    this.#query('[data-choose-variant]', (el) => {
      const variant = !this.photos.length ? 'first' : cfg.max === 1 ? 'replace' : 'more';
      el.hidden = el.dataset.chooseVariant !== variant;
    });
    this.#updateBar();
  }

  async #addFiles(files) {
    if (!files.length) return;
    this.#clearAlert('upload');
    const lib = await this.#uploadLib();
    if (!lib) return this.#alert(this.#message('network'), 'upload');
    const cfg = this.#subjectConfig();
    if (cfg.max === 1) files = files.slice(0, 1);
    else {
      const free = cfg.max - this.photos.length;
      if (files.length > free) {
        this.#alert(fill(this.s.upload?.tooMany, { count: cfg.max }), 'upload');
        files = files.slice(0, Math.max(0, free));
      }
    }
    for (const file of files) {
      const code = lib.checkFile(file, this.photoLimits);
      if (code) {
        this.#alert(this.s.upload?.errors?.[code] || this.s.errors?.upload_failed, 'upload');
        continue;
      }
      const photo = { id: (this.photoSeq += 1), file, url: URL.createObjectURL(file), status: 'preparing', uploaded: false, index: null };
      const previous = cfg.max === 1 ? this.photos : []; // one-photo subjects: the new photo replaces the old
      this.photos = cfg.max === 1 ? [photo] : [...this.photos, photo];
      this.#renderPhotos();
      this.#uploadStatus(this.s.upload?.preparing);
      try {
        Object.assign(photo, await lib.prepareImage(file, this.photoLimits), { status: 'ready' });
        previous.forEach((p) => {
          if (p.url) URL.revokeObjectURL(p.url);
          if (p.index !== null) this.warned.delete(p.index);
        });
        if (photo.small) announce(this.s.upload?.small); // a warning only: the photo is kept
      } catch (error) {
        this.#dropPhoto(photo);
        if (previous.length) this.photos = previous;
        this.#alert(this.s.upload?.errors?.[error.code] || this.s.upload?.errors?.unreadable, 'upload');
      }
      this.#renderPhotos();
    }
    this.#uploadStatus('');
  }

  #dropPhoto(photo) {
    if (photo.url) URL.revokeObjectURL(photo.url);
    if (photo.index !== null) this.warned.delete(photo.index);
    this.photos = this.photos.filter((p) => p !== photo);
  }

  #removePhoto(id) {
    const photo = this.photos.find((p) => p.id === id);
    if (!photo) return;
    this.#dropPhoto(photo);
    this.#renderPhotos();
    this.querySelector('[data-file-input]')?.focus();
  }

  #uploadStatus(message, progress = null) {
    this.#status('upload', message);
    this.#query('[data-upload-progress]', (bar) => {
      bar.hidden = progress === null;
      if (progress !== null) bar.value = Math.round(progress * 100);
    });
  }

  #bindDropzone() {
    const zone = this.querySelector('[data-dropzone]');
    if (!zone) return;
    const over = (on) => (event) => {
      event.preventDefault();
      zone.classList.toggle('is-dragover', on);
    };
    zone.addEventListener('dragenter', over(true));
    zone.addEventListener('dragover', over(true));
    zone.addEventListener('dragleave', over(false));
    zone.addEventListener('drop', (event) => {
      over(false)(event);
      this.#addFiles([...(event.dataTransfer?.files || [])]);
    });
  }

  #chooseAnother() {
    this.goTo('upload');
    if (this.#subjectConfig().max === 1) this.querySelector('[data-file-input]')?.click();
  }

  async #submitPhotos(el) {
    const rights = this.querySelector('[data-rights]');
    if (!this.photos.length) return this.#invalid(this.s.upload?.errors?.missing, this.querySelector('[data-step="upload"] [data-file-input]'));
    if (this.photos.some((p) => p.status !== 'ready')) return this.#invalid(this.s.upload?.preparing);
    if (!rights?.checked) return this.#fieldError('rights', true);
    this.gallery = Boolean(this.querySelector('[data-gallery]')?.checked);

    await this.#guard(el, async () => {
      if (!(await this.#ensureSession())) return;
      const unchanged =
        this.projectId &&
        this.projectSubject === this.subject &&
        this.projectSitters === this.#sitters() &&
        this.projectCount === this.photos.length &&
        this.photos.every((p) => p.uploaded);
      if (unchanged && this.projectReady) return this.goTo('style');
      try {
        if (!unchanged) {
          await this.#uploadPhotos(this.photos.filter((p) => !p.uploaded));
          track('photo_uploaded', { subject_type: this.subject, photo_count: this.photos.length });
        }
        this.#uploadStatus(this.s.check?.checking);
        const warnings = await this.#checkPhotos();
        this.#uploadStatus('');
        if (warnings.length) {
          this.#renderCheck(warnings);
          this.goTo('check');
        } else {
          announce(this.s.check?.passed);
          this.goTo('style');
        }
      } catch (error) {
        this.#uploadStatus('');
        if (error.code === 'unauthorized') return this.#sessionExpired();
        const upload = this.s.upload?.errors || {};
        // The answer to this step's Continue: shown in view while the customer is still on the step.
        if (error.code === 'too_many_photos') this.#alert(fill(this.s.upload?.tooMany, { count: this.#subjectConfig().max }), 'upload');
        else this.#alert(upload[error.code] || this.#message(error.code), 'upload');
        this.#reveal(null, 'upload');
      }
    });
  }

  async #uploadPhotos(dirty) {
    const lib = await this.#uploadLib();
    if (!lib) throw new ApiError('network');
    const count = this.photos.length;
    // Same project, not painted yet, same number of photos: replace only the changed ones.
    const canReplace =
      this.projectId && !this.attempts.length && this.projectSubject === this.subject && this.projectSitters === this.#sitters() && this.projectCount === count;
    let response;
    if (canReplace) {
      const used = new Set(this.photos.filter((p) => p.uploaded).map((p) => p.index));
      const free = [...Array(count).keys()].filter((i) => !used.has(i));
      dirty.forEach((p, i) => {
        p.index = free[i];
      });
      ({ data: response } = await this.api.call('uploads', {
        body: {
          generation_id: this.projectId,
          photos: dirty.map((p) => ({ mime: p.mime, size: p.blob.size, index: p.index })),
          marketing_consent: this.gallery,
        },
      }));
    } else {
      this.photos.forEach((p, i) => {
        p.index = i;
        p.uploaded = false;
      });
      dirty = this.photos;
      ({ data: response } = await this.api.call('uploads', {
        body: {
          subject_type: this.subject,
          subject_count: this.#sitters(),
          photos: this.photos.map((p) => ({ mime: p.mime, size: p.blob.size })),
          rights_confirmed: true,
          marketing_consent: this.gallery,
          locale: this.locale,
        },
      }));
      this.projectId = response.project_id || response.generation_id;
      this.resumeLater = null;
      this.projectSubject = this.subject;
      this.projectCount = count;
      this.projectSitters = this.#sitters();
      this.attempts = [];
      this.attemptId = null;
      this.retriesLeft = null;
    }
    this.projectReady = false;
    this.#save();
    const total = dirty.reduce((sum, p) => sum + p.blob.size, 0) || 1;
    const loaded = new Map();
    const report = () => {
      const done = [...loaded.values()].reduce((a, b) => a + b, 0) / total;
      this.#uploadStatus(fill(this.s.upload?.progress, { percent: Math.min(100, Math.round(done * 100)) }), done);
    };
    report();
    await Promise.all(
      dirty.map(async (photo) => {
        const target = (response.uploads || []).find((u) => u.index === photo.index);
        await lib.uploadPhoto(target, photo.blob, (fraction) => {
          loaded.set(photo.id, fraction * photo.blob.size);
          report();
        });
        photo.uploaded = true;
        this.warned.delete(photo.index);
      })
    );
    announce(this.s.upload?.done);
  }

  async #checkPhotos() {
    const { data } = await this.api.call('photo-check', { body: { generation_id: this.projectId }, timeout: 90000 });
    if (!data.ready) throw new ApiError('photo_missing');
    this.projectReady = true;
    this.#save();
    const items = [];
    const seen = new Set();
    (data.photos || []).forEach((photo) => {
      (photo.warnings || []).forEach((code) => {
        if (!this.s.check?.warnings?.[code]) return;
        seen.add(code);
        this.warned.add(photo.index);
        items.push({ index: photo.index, code });
      });
    });
    (data.warnings || []).forEach((code) => {
      if (!seen.has(code) && this.s.check?.warnings?.[code]) items.push({ index: null, code });
    });
    return items;
  }

  #renderCheck(items) {
    const list = this.querySelector('[data-check-list]');
    if (!list) return;
    list.replaceChildren();
    const groups = new Map();
    items.forEach((item) => {
      if (!groups.has(item.index)) groups.set(item.index, []);
      groups.get(item.index).push(item.code);
    });
    groups.forEach((codes, index) => {
      const node = this.#clone('check-item');
      if (!node) return;
      const photo = index === null ? null : this.photos.find((p) => p.index === index);
      const img = node.querySelector('img');
      if (photo?.url) {
        img.src = photo.url;
        img.alt = fill(this.s.upload?.photoAlt, { number: index + 1 });
      } else img.remove();
      const label = node.querySelector('[data-label]');
      if (index === null || this.photos.length < 2) label.remove();
      else label.textContent = fill(this.s.check?.photo, { number: index + 1 });
      const ul = node.querySelector('ul');
      codes.forEach((code) => {
        const li = document.createElement('li');
        li.textContent = this.s.check.warnings[code];
        ul.append(li);
      });
      list.append(node);
    });
  }

  /** subject_count sent to POST uploads: the family stepper's 3 or 4, otherwise fixed by the subject. */
  #sitters() {
    return this.subject === 'family' ? this.familyCount : this.#subjectConfig().count;
  }

  /* ---------- step 3: style ---------- */
  async #loadStyles() {
    try {
      const { data } = await this.api.call('styles', { method: 'GET', timeout: 15000 });
      const handles = data.handles || (data.styles || []).map((s) => s.handle);
      this.supported = new Set(handles);
      this.backendStyles = data.styles || [];
    } catch {
      this.supported = null; // unknown: keep every card rendered by Liquid
    }
    if (this.step === 'style') this.#renderStyles();
  }

  #renderStyles() {
    const list = this.querySelector('[data-style-list]');
    let cards = [...this.querySelectorAll('[data-style-card]')];
    if (!cards.length && this.backendStyles?.length && list) {
      // No atelier_style entries in Shopify yet: build cards from the backend list.
      this.backendStyles
        .slice()
        .sort((a, b) => (a.sort || 0) - (b.sort || 0))
        .forEach((style) => {
          const card = this.#clone('style');
          if (!card) return;
          card.dataset.handle = style.handle;
          card.dataset.name = style.name || style.handle;
          card.querySelector('[data-style]').value = style.handle;
          card.querySelector('[data-name]').textContent = style.name || style.handle;
          card.querySelector('[data-initial]').textContent = (style.name || style.handle).charAt(0);
          card.querySelector('[data-description]').textContent = style.description || '';
          list.append(card);
        });
      cards = [...this.querySelectorAll('[data-style-card]')];
    }
    const supported = this.supported || this.configStyles; // GET /styles, else /config style_handles
    const visible = cards.filter((card) => !supported?.size || supported.has(card.dataset.handle));
    cards.forEach((card) => {
      card.hidden = !visible.includes(card);
      card.querySelector('[data-style]').disabled = card.hidden;
    });
    const choice = visible.find((card) => card.dataset.handle === this.styleHandle);
    if (choice) {
      choice.querySelector('[data-style]').checked = true;
      const preselected = this.styleHandle === this.params.style && !this.attempts.length;
      this.#query('[data-style-note]', (note) => {
        note.hidden = !preselected;
        note.textContent = fill(this.s.style?.note, { style: choice.dataset.name });
      });
    }
    this.#query('[data-allowance]', (el) => {
      el.hidden = this.attempts.length > 0;
    });
  }

  #styleName(handle) {
    const card = [...this.querySelectorAll('[data-style-card]')].find((c) => c.dataset.handle === handle);
    return card?.dataset.name || this.backendStyles?.find((s) => s.handle === handle)?.name || handle || '';
  }

  /* ---------- step 4: generate ---------- */
  async #generate(el, { from, style } = {}) {
    const chosen = style || this.querySelector('[data-style]:checked')?.value;
    if (!chosen) return this.#invalid(this.s.style?.error, this.#firstChoice('style', 'data-style'));
    await this.#guard(el, async () => {
      if (!(await this.#ensureSession())) return;
      if (!this.projectId || !this.projectReady) {
        this.goTo('upload');
        return this.#alert(this.s.upload?.errors?.missing);
      }
      try {
        const { data } = await this.api.call('generations', {
          body: { generation_id: this.projectId, style: chosen, marketing_consent: this.gallery },
        });
        const previous = this.#current();
        this.styleHandle = data.style || chosen;
        if (typeof data.retries_left === 'number') this.retriesLeft = data.retries_left;
        this.attempts.push({ job_id: data.job_id, style: this.styleHandle, attempt: data.attempt, status: data.status || 'queued' });
        this.activeJobId = data.job_id;
        if (from === 'preview' && previous) track('preview_regenerated', { style: this.styleHandle, attempt: data.attempt });
        if (from === 'style') track('style_selected', { style: this.styleHandle });
        track('generation_started', { style: this.styleHandle, attempt: data.attempt, subject_type: this.subject });
        this.goTo('generate');
        this.#pollGeneration(data.job_id);
      } catch (error) {
        await this.#generationRefused(error, from);
      }
    });
  }

  /**
   * The answer to a refused generation. `from` is the step the customer tapped on: the message goes to that
   * step (or to where the refusal sends them), so a Back tapped while the request ran never gets it shown,
   * or scrolled to, on the step they went to.
   */
  async #generationRefused(error, from = this.step) {
    const code = error.code;
    let step = from;
    if (code === 'unauthorized') return this.#sessionExpired();
    if (code === 'job_in_progress') {
      try {
        const { data } = await this.api.call(`projects/${this.projectId}`);
        const active = (data.project?.attempts || []).find((a) => a.status === 'queued' || a.status === 'generating');
        if (active) {
          if (!this.#attempt(active.job_id)) this.attempts.push({ ...active });
          this.activeJobId = active.job_id;
          this.goTo('generate');
          this.#status('generate', this.s.errors?.job_in_progress);
          return this.#pollGeneration(active.job_id);
        }
      } catch {
        /* fall through to the message */
      }
    }
    if (code === 'retry_limit_reached') {
      this.retriesLeft = 0;
      if (this.attempts.some((a) => a.status === 'ready')) {
        this.goTo('preview');
        return this.#alert(this.#message(code));
      }
    }
    if (code === 'not_found') {
      this.#reset();
      this.goTo('upload');
      step = 'upload';
    }
    if (code === 'photo_check_required') this.projectReady = false;
    this.#alert(this.#message(code), step);
    this.#reveal(null, step); // the answer to the customer's own tap, in view while they are still on that step
  }

  #attempt(id) {
    return this.attempts.find((a) => a.job_id === id) || null;
  }

  #current() {
    const a = this.#attempt(this.attemptId);
    return a?.status === 'ready' ? a : null;
  }

  #renderGenerate(failed, code) {
    this.#query('[data-generate-loading]', (el) => {
      el.hidden = failed;
    });
    this.#query('[data-generate-failed]', (el) => {
      el.hidden = !failed;
    });
    this.#query('[data-action="retry"][data-in="generate"]', (el) => {
      el.hidden = code === 'content_refused' || code === 'retry_limit_reached' || this.retriesLeft === 0;
    });
    this.#query('[data-action="other-style"][data-in="generate"]', (el) => {
      el.hidden = code === 'retry_limit_reached' || this.retriesLeft === 0;
    });
    if (!failed) this.#status('generate', isOffline() ? this.s.generate?.offline : '');
    this.#syncCta();
  }

  async #pollGeneration(jobId) {
    const run = (this.pollRun += 1);
    const started = Date.now();
    let delay = this.pollMs;
    let failures = 0;
    while (run === this.pollRun && this.isConnected) {
      await wait(delay);
      if (run !== this.pollRun) return;
      if (isOffline()) {
        this.#status('generate', this.s.generate?.offline);
        await onceOnline();
        continue;
      }
      try {
        const { data } = await this.api.call(`jobs/${jobId}`);
        if (run !== this.pollRun) return;
        failures = 0;
        if (typeof data.retries_left === 'number') this.retriesLeft = data.retries_left;
        if (data.status === 'ready') return this.#attemptReady(jobId, data);
        if (data.status === 'failed') return this.#attemptFailed(jobId, data);
        delay = Math.max(1000, Number(data.poll_after_ms) || this.pollMs);
        this.#status('generate', Date.now() - started > LATER_MS ? this.s.generate?.later : '');
      } catch (error) {
        if (run !== this.pollRun) return;
        if (error.code === 'unauthorized') return this.#sessionExpired();
        if (error.code === 'not_found') return this.#attemptFailed(jobId, { error: { code: 'generation_failed' } });
        failures += 1;
        delay = Math.min(15000, this.pollMs * 2 ** Math.min(failures, 3));
        // Offline or maintenance: say so and keep watching (the job carries on server-side).
        const code = error.code === 'network' || error.code === 'maintenance' ? error.code : null;
        this.#status('generate', code ? this.#message(code) : this.s.generate?.later);
      }
    }
  }

  #attemptReady(jobId, data) {
    let attempt = this.#attempt(jobId);
    if (!attempt) {
      attempt = { job_id: jobId };
      this.attempts.push(attempt);
    }
    Object.assign(attempt, {
      status: 'ready',
      preview_url: data.preview_url,
      thumb_url: data.thumb_url,
      style: data.style || attempt.style,
      attempt: data.attempt ?? attempt.attempt,
    });
    this.activeJobId = null;
    this.attemptId = jobId;
    this.printJob = null;
    this.added = false;
    track('generation_completed', { style: attempt.style, attempt: attempt.attempt });
    this.goTo('preview');
    announce(this.s.preview?.ready);
  }

  #attemptFailed(jobId, data) {
    const attempt = this.#attempt(jobId);
    if (attempt) attempt.status = 'failed';
    this.activeJobId = null;
    const code = data.error?.code || 'generation_failed';
    if (this.step !== 'generate') this.goTo('generate');
    this.#renderGenerate(true, code);
    this.#alert(this.#message(code), 'generate');
    this.#save();
  }

  /* ---------- step 5: preview ---------- */
  #renderPreview() {
    const attempt = this.#current() || this.attempts.filter((a) => a.status === 'ready').pop();
    if (!attempt) return;
    this.attemptId = attempt.job_id;
    const img = this.querySelector('[data-preview-img]');
    if (img && img.dataset.job !== attempt.job_id) {
      img.dataset.job = attempt.job_id;
      img.dataset.refreshed = '';
      img.onerror = () => this.#refreshPreview(img, attempt);
      img.src = attempt.preview_url || '';
      img.alt = fill(this.s.preview?.alt, { style: this.#styleName(attempt.style) });
      blurReveal(img);
    }
    const ready = this.attempts.filter((a) => a.status === 'ready');
    const versions = this.querySelector('[data-versions]');
    this.#query('[data-versions-wrap]', (el) => {
      el.hidden = ready.length < 2;
    });
    if (versions) {
      versions.replaceChildren();
      ready.forEach((a, i) => {
        const node = this.#clone('version');
        if (!node) return;
        const input = node.querySelector('[data-version]');
        input.value = a.job_id;
        input.checked = a.job_id === attempt.job_id;
        const thumb = node.querySelector('img');
        if (a.thumb_url) thumb.src = a.thumb_url;
        else thumb.remove();
        node.querySelector('[data-label]').textContent = fill(this.s.preview?.version, { number: i + 1 });
        versions.append(node);
      });
    }
    const left = this.retriesLeft;
    const exhausted = left === 0;
    this.#query('[data-retry-wrap]', (el) => {
      el.hidden = exhausted;
    });
    this.#query('[data-limit]', (el) => {
      el.hidden = !exhausted;
    });
    this.#query('[data-retries]', (el) => {
      el.textContent = left > 0 ? this.s.preview?.retries?.[left] || '' : '';
    });
  }

  async #refreshPreview(img, attempt) {
    if (img.dataset.refreshed) return;
    img.dataset.refreshed = 'true';
    try {
      const { data } = await this.api.call(`jobs/${attempt.job_id}`);
      if (data.preview_url) {
        attempt.preview_url = data.preview_url;
        if (img.dataset.job === attempt.job_id) {
          img.src = data.preview_url;
          img.hidden = false;
        }
      }
    } catch {
      /* the frame keeps its placeholder */
    }
  }

  #chooseVersion(jobId) {
    const attempt = this.#attempt(jobId);
    if (!attempt || attempt.status !== 'ready' || jobId === this.attemptId) return;
    this.attemptId = jobId;
    this.styleHandle = attempt.style || this.styleHandle;
    this.printJob = null;
    this.added = false;
    this.#renderPreview();
    this.#save();
  }

  #openCompare(trigger) {
    const ready = this.attempts.filter((a) => a.status === 'ready');
    const current = this.#current();
    if (ready.length < 2 || !current) return;
    const other = [...ready].reverse().find((a) => a.job_id !== current.job_id);
    const dialog = this.#dialog('compare');
    const slots = dialog?.querySelectorAll('[data-compare-slot]') || [];
    [current, other].forEach((attempt, i) => {
      const slot = slots[i];
      if (!slot) return;
      const img = slot.querySelector('img');
      // Only the watermarked preview is shown here, never the clean thumbnail: without a (fresh)
      // preview URL the slot stays an empty frame until #refreshPreview brings one.
      img.dataset.job = attempt.job_id;
      img.dataset.refreshed = '';
      img.onerror = () => this.#refreshPreview(img, attempt);
      img.alt = fill(this.s.preview?.alt, { style: this.#styleName(attempt.style) });
      if (attempt.preview_url) {
        img.hidden = false;
        img.src = attempt.preview_url;
      } else {
        img.hidden = true;
        img.src = 'data:,';
        this.#refreshPreview(img, attempt);
      }
      slot.querySelector('[data-label]').textContent = fill(this.s.preview?.version, { number: ready.indexOf(attempt) + 1 });
      slot.querySelector('[data-action="choose-version"]').dataset.job = attempt.job_id;
    });
    dialog?.show(trigger);
  }

  #accept(el) {
    const attempt = this.#current();
    if (!attempt) return;
    track('preview_accepted', { style: attempt.style, attempt: attempt.attempt, ...trackVariant(this.lockedVariant || this.#variant()) });
    if (this.lockedVariant) {
      if (this.format === 'digital') return this.#attachDigital(el);
      this.goTo('prepare');
      return this.#startPrint();
    }
    const preset = this.presetVariant;
    if (preset && PHYSICAL.includes(preset.product_type) && this.format === preset.product_type) {
      this.presetVariant = null; // used once; the choice stays editable
      return this.goTo('size');
    }
    return this.goTo('format');
  }

  #newPhoto() {
    this.pollRun += 1;
    const subject = this.subject;
    this.photos.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    this.#reset();
    this.subject = subject;
    this.goTo('upload');
  }

  /* ---------- step 6: format ---------- */
  #variants(type) {
    return Object.values(this.map)
      .filter((v) => v.product_type === type)
      .sort((a, b) => {
        const da = dims(a.size) || [0, 0];
        const db = dims(b.size) || [0, 0];
        return da[0] * da[1] - db[0] * db[1];
      });
  }

  #variant() {
    return (this.variantKey && this.map[this.variantKey]) || null;
  }

  #renderFormats() {
    const list = this.querySelector('[data-format-list]');
    if (!list) return;
    if (!list.children.length) {
      FORMAT_ORDER.forEach((type) => {
        const variants = this.#variants(type);
        if (!variants.length) return;
        const available = variants.filter((v) => v.available !== false);
        const card = this.#clone('format');
        if (!card) return;
        const input = card.querySelector('[data-format]');
        input.value = type;
        input.disabled = !available.length;
        card.querySelector('[data-name]').textContent = this.s.format?.names?.[type] || type;
        card.querySelector('[data-tagline]').textContent = this.s.format?.taglines?.[type] || '';
        const prices = (available.length ? available : variants).slice().sort((a, b) => (a.price || 0) - (b.price || 0));
        const cheapest = prices[0];
        const price = cheapest?.price_formatted || '';
        card.querySelector('[data-price]').textContent = variants.length > 1 ? fill(this.s.format?.from, { price }) : price;
        card.querySelectorAll('[data-icon]').forEach((icon) => {
          icon.hidden = icon.dataset.icon !== (type === 'digital' ? 'digital' : 'frame');
        });
        list.append(card);
      });
    }
    const empty = !list.children.length;
    this.#query('[data-format-empty]', (el) => {
      el.hidden = !empty;
    });
    this.#query('[data-format]', (input) => {
      input.checked = input.value === this.format;
    });
    this.#toggleConsent(this.format === 'digital');
  }

  #toggleConsent(show) {
    this.#query('[data-consent-wrap]', (el) => {
      el.hidden = !show || Boolean(this.attach);
    });
  }

  #nextFromFormat(el) {
    const type = this.querySelector('[data-format]:checked')?.value;
    if (!type) return this.#invalid(this.s.format?.error, this.#firstChoice('format', 'data-format'));
    if (type === 'digital') {
      const consent = this.querySelector('[data-digital-consent]');
      if (!this.attach && !consent?.checked) return this.#fieldError('consent', true);
    }
    if (this.format !== type) {
      this.variantKey = null;
      this.printJob = null;
    }
    this.format = type;
    if (type === 'digital') this.variantKey = this.#variants('digital')[0]?.key || 'digital';
    // The Digital Artwork has no size step: its variant is known here (ViewContent / view_item in the pixel).
    track('product_type_selected', { product_type: type, ...(type === 'digital' ? trackVariant(this.#variant()) : {}) });
    if (type === 'digital') {
      if (this.attach) return this.#attachDigital(el);
      return this.goTo('cart');
    }
    return this.goTo('size');
  }

  /* ---------- step 7: size ---------- */
  #renderSizes() {
    const list = this.querySelector('[data-size-list]');
    if (!list) return;
    if (list.dataset.format !== this.format) {
      list.replaceChildren();
      list.dataset.format = this.format || '';
      const scaleMax = 48; // inches represented by the full width/height of the square wall box
      this.#variants(this.format).forEach((v) => {
        const card = this.#clone('size');
        if (!card) return;
        const input = card.querySelector('[data-size]');
        input.value = v.key;
        input.disabled = v.available === false;
        const inches = dims(v.size) || [0, 0];
        card.querySelector('[data-art]').style.cssText = `--w:${(inches[0] / scaleMax) * 100}%;--h:${(inches[1] / scaleMax) * 100}%`;
        card.querySelector('[data-name]').textContent = this.s.size?.names?.[v.size] || v.size_label || v.title || v.size;
        card.querySelector('[data-dimensions]').textContent = v.title || v.size;
        card.querySelector('[data-use]').textContent = v.available === false ? this.s.size?.unavailable : this.s.size?.uses?.[v.size] || '';
        card.querySelector('[data-price]').textContent = v.price_formatted || '';
        const ratio = v.ratio ? String(v.ratio) : '';
        card.querySelector('[data-ratio]').textContent = ratio ? fill(this.s.size?.ratio, { ratio }) : '';
        const badge = card.querySelector('[data-badge]');
        const text = v.size === this.config.popularSize ? this.s.size?.popular : v.size === this.config.recommendedSize ? this.s.size?.recommended : '';
        badge.textContent = text || '';
        badge.hidden = !text;
        list.append(card);
      });
    }
    this.#query('[data-size]', (input) => {
      input.checked = input.value === this.variantKey;
    });
    const attempt = this.#current();
    this.#query('[data-crop-img]', (img) => {
      if (attempt?.preview_url && img.dataset.job !== attempt.job_id) {
        img.dataset.job = attempt.job_id;
        img.src = attempt.preview_url;
        img.alt = fill(this.s.preview?.alt, { style: this.#styleName(attempt.style) });
      }
    });
    this.#updateCrop(this.variantKey);
  }

  #updateCrop(key) {
    const v = this.map[key];
    const box = this.querySelector('[data-crop-box]');
    if (!box) return;
    const safe = dims(v?.safe_px) || dims(v?.ratio) || dims(v?.size);
    box.hidden = !safe;
    if (!safe) return;
    const c = cropBox(safe[0], safe[1]);
    box.style.cssText = `--x:${c.x * 100}%;--y:${c.y * 100}%;--w:${c.w * 100}%;--h:${c.h * 100}%`;
    this.#query('[data-crop-ratio]', (el) => {
      el.textContent = v?.ratio ? fill(this.s.size?.ratio, { ratio: v.ratio }) : '';
    });
  }

  #nextFromSize() {
    const key = this.querySelector('[data-size]:checked')?.value;
    const v = this.map[key];
    if (!v) return this.#invalid(this.s.size?.error, this.#firstChoice('size', 'data-size'));
    if (this.variantKey !== key) {
      this.printJob = null;
      this.added = false;
    }
    this.variantKey = key;
    track('size_selected', { product_type: v.product_type, size: v.size, ...trackVariant(v) });
    const job = this.printJob;
    if (job?.ref && job.sig && job.variant_key === key && job.generation_id === this.attemptId) return this.goTo('cart');
    this.goTo('prepare');
    return this.#startPrint();
  }

  /* ---------- step 8: prepare print ---------- */
  #renderPrepare(failed) {
    this.#query('[data-prepare-loading]', (el) => {
      el.hidden = failed;
    });
    this.#query('[data-prepare-failed]', (el) => {
      el.hidden = !failed;
    });
    this.#syncCta();
  }

  async #startPrint() {
    const v = this.#variant();
    const attempt = this.#current();
    if (!v || !attempt || this.busy) return;
    if (this.step !== 'prepare') this.goTo('prepare');
    this.#renderPrepare(false);
    this.#clearAlert('prepare');
    this.#status('prepare', '');
    track('print_preparation_started', { product_type: v.product_type, size: v.size });
    try {
      if (!(await this.#ensureSession())) return;
      const body = { generation_id: attempt.job_id, variant_key: v.key, variant_id: String(v.id) };
      if (this.attach) body.attach = this.attach;
      const { data } = await this.api.call('print-jobs', { body });
      this.printJob = { job_id: data.job_id, variant_key: v.key, generation_id: attempt.job_id, status: data.status };
      this.#save();
      if (data.status === 'ready' && printRefOf(data)) return this.#printReady(data);
      if (data.status === 'failed') return this.#printFailed(data.error?.code);
      return this.#pollPrint(data.job_id);
    } catch (error) {
      if (error.code === 'unauthorized') return this.#sessionExpired();
      return this.#printFailed(error.code);
    }
  }

  async #pollPrint(jobId) {
    const run = (this.pollRun += 1);
    let delay = this.pollMs;
    let failures = 0;
    this.#renderPrepare(false);
    while (run === this.pollRun && this.isConnected) {
      await wait(delay);
      if (run !== this.pollRun) return;
      if (isOffline()) {
        this.#status('prepare', this.s.generate?.offline);
        await onceOnline();
        continue;
      }
      try {
        const { data } = await this.api.call(`print-jobs/${jobId}`);
        if (run !== this.pollRun) return;
        failures = 0;
        this.#status('prepare', '');
        if (data.status === 'ready' && printRefOf(data)) return this.#printReady(data);
        if (data.status === 'failed') return this.#printFailed(data.error?.code);
        delay = Math.max(1000, Number(data.poll_after_ms) || this.pollMs);
      } catch (error) {
        if (run !== this.pollRun) return;
        if (error.code === 'unauthorized') return this.#sessionExpired();
        if (error.code === 'not_found') return this.#printFailed('print_failed');
        failures += 1;
        delay = Math.min(15000, this.pollMs * 2 ** Math.min(failures, 3));
        if (error.code === 'network' || error.code === 'maintenance') this.#status('prepare', this.#message(error.code));
      }
    }
  }

  #printReady(data) {
    const v = this.#variant();
    this.printJob = {
      ...this.printJob,
      job_id: data.job_id || this.printJob?.job_id,
      status: 'ready',
      ref: printRefOf(data), // opaque (AR-05): copied into the bag line, never fetched or shown
      sig: data.sig,
      width: data.width,
      height: data.height,
      variant_key: data.variant_key || v?.key,
      generation_id: data.generation_id || this.attemptId,
    };
    track('print_preparation_completed', { product_type: v?.product_type, size: v?.size });
    announce(this.s.prepare?.ready);
    this.goTo(this.attach ? 'attached' : 'cart');
  }

  #printFailed(code) {
    // Checkout stays closed: never fall back to the preview (spec §58).
    this.printJob = null;
    this.#renderPrepare(true);
    const message = this.attach && (code === 'invalid_request' || code === 'not_found') ? this.s.attach?.error : this.s.prepare?.error;
    this.#alert(code === 'network' ? this.#message('network') : message, 'prepare');
    this.#save();
  }

  /* ---------- attach flow (portrait-missing email) ---------- */
  async #attachDigital(el) {
    const step = this.step; // preview (a locked variant) or format: where an error belongs, even after a Back
    const v = this.#variant() || this.#variants('digital')[0];
    const attempt = this.#current();
    if (!v || !attempt) return;
    await this.#guard(el, async () => {
      try {
        if (!(await this.#ensureSession())) return;
        await this.api.call('print-jobs/digital', { body: { generation_id: attempt.job_id, variant_id: String(v.id), attach: this.attach } });
        this.goTo('attached');
      } catch (error) {
        if (error.code === 'unauthorized') return this.#sessionExpired();
        this.#alert(error.code === 'network' ? this.#message('network') : this.s.attach?.error, step);
        this.#reveal(null, step);
      }
    });
  }

  /* ---------- step 9: add to bag ---------- */
  #renderCart() {
    const v = this.#variant();
    const attempt = this.#current();
    const physical = PHYSICAL.includes(v?.product_type);
    const set = (name, text) =>
      this.#query(`[data-summary="${name}"]`, (el) => {
        el.textContent = text || '';
        el.closest('[data-row]')?.toggleAttribute('hidden', !text);
      });
    set('format', this.s.format?.names?.[v?.product_type]);
    set('size', physical ? v?.title || v?.size : '');
    set('style', this.#styleName(attempt?.style));
    set('sitters', this.s.subjects?.[this.subject]);
    set('price', v?.price_formatted);
    this.#query('[data-summary-thumb]', (img) => {
      if (attempt?.thumb_url) img.src = attempt.thumb_url;
      img.hidden = !attempt?.thumb_url;
    });
    this.#query('[data-added]', (el) => {
      el.hidden = !this.added;
    });
    this.#query('[data-action="add-to-cart"]', (el) => {
      el.hidden = this.added;
    });
    this.#syncCta();
  }

  /**
   * Reads the bag for the cart step (wave-2 #13): the saved state does not say whether this portrait is
   * already in it (after a resume, or back and forward through the steps), so /cart.js decides. Only the
   * latest read counts, and an add or a cart event in the meantime wins over it.
   */
  #checkBag() {
    const read = (this.bagRead = (this.bagRead || 0) + 1);
    getCart().then((cart) => {
      if (cart && read === this.bagRead && this.isConnected) this.#syncAdded(cart);
    });
  }

  /** this.added from a cart: the physical line that holds this print job, or the digital line of this version. */
  #syncAdded(cart) {
    const v = this.#variant();
    const attempt = this.#current();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let added = false;
    if (v && attempt) {
      added = PHYSICAL.includes(v.product_type)
        ? items.some((item) => holdsPrint(item, this.printJob))
        : items.some((item) => String(item.variant_id) === String(v.id) && item.properties?._generation_id === attempt.job_id);
    }
    if (added === this.added) return;
    this.added = added;
    if (this.step === 'cart') this.#renderCart();
    // The open drawer gives focus back on close to the button that opened it. If that was our "View bag"
    // (now hidden after a removal) or "Add to bag" (hidden after an Undo), hand it the step's visible button
    // instead, so keyboard and screen-reader users do not land on <body>.
    const drawer = document.querySelector('ar-drawer[data-ar-cart-drawer]');
    if (drawer?.isOpen && drawer.trigger && this.contains(drawer.trigger) && drawer.trigger.closest('[hidden]')) {
      drawer.trigger = this.#primaryButton() || drawer.trigger;
    }
  }

  #isPhysicalLine(item) {
    const type = item?.properties?._product_type;
    if (type) return PHYSICAL.includes(type);
    return Object.values(this.map).some((v) => PHYSICAL.includes(v.product_type) && String(v.id) === String(item?.variant_id));
  }

  #confirmReplace(trigger) {
    const dialog = this.#dialog('replace');
    if (!dialog?.show) return Promise.resolve(false);
    return new Promise((resolve) => {
      let decided = false;
      const confirm = dialog.querySelector('[data-replace-confirm]');
      const cleanup = () => {
        confirm?.removeEventListener('click', onConfirm);
        dialog.removeEventListener('ar:modal:close', onClose);
      };
      const onConfirm = (event) => {
        event.preventDefault();
        event.stopPropagation();
        decided = true;
        cleanup();
        dialog.hide();
        resolve(true);
      };
      const onClose = () => {
        cleanup();
        if (!decided) resolve(false);
      };
      confirm?.addEventListener('click', onConfirm);
      dialog.addEventListener('ar:modal:close', onClose);
      dialog.show(trigger);
    });
  }

  async #addToCart(el) {
    const v = this.#variant();
    const attempt = this.#current();
    if (!v || !attempt) return this.goTo(attempt ? 'format' : 'preview');
    const physical = PHYSICAL.includes(v.product_type);
    const job = this.printJob;
    if (physical && !(job?.ref && job.sig && job.variant_key === v.key && job.generation_id === attempt.job_id)) {
      this.goTo('prepare');
      return this.#startPrint();
    }
    const step = this.step; // 'cart': an error answers this tap there, even if the customer went Back meanwhile
    await this.#guard(el, async () => {
      this.#clearAlert('cart');
      try {
        if (!(await this.#ensureSession())) return;
        const properties = {
          _preview_url: attempt.thumb_url || '',
          _generation_id: attempt.job_id,
          _subject_type: this.subject || '',
          _subject_count: String(this.projectSitters || this.#sitters()),
          _style: attempt.style || this.styleHandle || '',
          _product_type: v.product_type,
          _size: physical ? v.size : 'digital',
          _face_enhance: String(Boolean(SUBJECTS[this.subject]?.face)),
        };
        const attributes = { _lang: this.locale };
        let replacePhysical = false;
        if (physical) {
          // The opaque print reference, exactly as the backend sent it (_sig signs this string).
          properties._royal_portrait = job.ref;
          properties._print_job_id = job.job_id;
          properties._sig = job.sig;
          attributes.royal_portrait = job.ref;
          const cart = await getCart();
          const conflict = (cart?.items || []).some((item) => this.#isPhysicalLine(item) && !holdsPrint(item, job));
          if (conflict) {
            replacePhysical = await this.#confirmReplace(el);
            if (!replacePhysical) return;
          }
        } else {
          const { data } = await this.api.call('print-jobs/digital', { body: { generation_id: attempt.job_id, variant_id: String(v.id) } });
          properties._download_token = randomToken(16);
          properties._sig = data.sig;
        }
        const add = await this.#cartAdd();
        const request = {
          variantId: v.id,
          properties,
          attributes,
          physical,
          open: false,
          isPhysical: (item) => this.#isPhysicalLine(item),
          isSame: physical ? (item) => holdsPrint(item, job) : undefined,
        };
        try {
          await add({ ...request, replacePhysical });
        } catch (error) {
          // cart.js enforces the one-physical-portrait rule too: ask, then replace.
          if (error?.code !== 'one_physical_portrait' || replacePhysical) throw error;
          if (!(await this.#confirmReplace(el))) return;
          await add({ ...request, replacePhysical: true });
        }
        this.bagRead = (this.bagRead || 0) + 1; // a /cart.js read started before this add is out of date
        this.added = true;
        this.#renderCart();
        this.#save();
        toast(this.s.cart?.added, { tone: 'success' });
        // `el` (Add to bag) was just hidden by #renderCart: the drawer gives focus back to "View bag" on close.
        openCart(this.querySelector('[data-step="cart"] [data-action="view-bag"]') || el);
      } catch (error) {
        if (error.code === 'unauthorized') return this.#sessionExpired();
        this.#alert(error.code === 'network' ? this.#message('network') : this.s.cart?.error || this.#message(error.code), step);
        this.#reveal(null, step);
      }
    });
  }

  /* ---------- start over / expiry ---------- */
  #startOver() {
    this.pollRun += 1;
    this.photos.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    this.#reset();
    this.subject = null;
    this.familyCount = FAMILY_MIN;
    this.styleHandle = this.params.style;
    if (!this.lockedVariant) {
      this.format = this.presetVariant?.product_type || null;
      this.variantKey = this.presetVariant?.key || null;
    }
    this.#query('input[type="checkbox"], input[type="radio"]', (el) => {
      el.checked = false;
    });
    this.gallery = false;
    this.#query('[data-format-list], [data-size-list], [data-versions], [data-check-list]', (el) => {
      el.replaceChildren();
      delete el.dataset.format;
    });
    this.#showResume(false);
    this.goTo('subject');
  }

  #sessionExpired() {
    this.api.token = null;
    this.#startOver();
    this.#alert(this.s.errors?.unauthorized, 'subject');
  }
}

if (!customElements.get('ar-creator')) customElements.define('ar-creator', ARCreator);
